/* eslint-disable max-len */
import { Promise } from 'bluebird';
import React from 'react';
import { withTranslation } from 'react-i18next';

import { util, constant } from '@helper';

import {
  Form,
  FormInstance,
  Modal,
  Schema,
  Radio,
  RadioGroup,
  Input,
  Stack,
  Button,
  FlexboxGrid,
  Col,
  toaster,
  Steps,
  Panel,
  Row,
  SelectPicker,
  DatePicker,
  HeadingGroup,
  Heading,
  Text,
  Loader,
  IconButton,
  InlineEdit,
  Avatar,
  List,
  Placeholder,
  Checkbox,
} from 'rsuite';
import _ from 'lodash';
import { FaArrowRight, FaEye, FaPlus, FaTrash } from 'react-icons/fa';
import { Icon, Plus } from '@rsuite/icons';
import { CVMakerAPI, EventTrackerAPI, MiscAPI, OrderAPI } from '@services/api';
import { ItemDataType } from 'rsuite/esm/MultiCascadeTree';
import {
  BottomDocker,
  ResumePreviewer,
  ResumeTemplate,
} from '@common/components';
import ReactQuill from 'react-quill';
import moment from 'moment';
import AuthStorage from '@common/services/authStorage';
import {
  CertificationData,
  EducationData,
  ExperienceData,
  LanguageData,
  PersonalData,
  ProfessionalData,
  ProjectData,
  SkillData,
  VolunteerData,
} from '@common/types';
import SessionStorage from '@common/services/sessionStorage';

const { StringType, ArrayType, ObjectType } = Schema.Types;

type CVPayload = {
  name: string; // CV Filename
  title?: string;
  description: string;
  experience: string;
  experienceData: {
    data: Array<ExperienceData>;
  };
  educationData: {
    data: Array<EducationData>;
  };
  extraSectionData: {
    certifications: CertificationData[];
    languages: LanguageData[];
    projects: ProjectData[];
    volunteers: VolunteerData[];
  };
  skillData: SkillData;
  personalData: PersonalData;
  professionalData: ProfessionalData;
};

type AIRecommendation = {
  workProfile?: Array<string>;
  workExperience?: object | Map<string, Array<string>>;
  educations?: object | Map<string, Array<string>>;
  skills?: object | Map<string, string>;
};

type State = {
  payload: CVPayload | object | undefined;
  recommendations: AIRecommendation;
  step: number;
  isLoading: boolean;
  countries: Array<object>;
  cities: Array<object>;
  phoneCodes: Array<object>;
  jobTypes: Array<object>;
  majors: Array<object>;
  degrees: Array<object>;
  universities: Array<object>;
  promo?: object;

  loadingSummary: boolean;
  loadingSkills: boolean;
  loadingExp?: object;
  loadingEdu?: object;

  toggleCVPreview: boolean;
  toggleExperience: boolean[];
  toggleEdu: boolean[];
};

const { withRouter } = util;
const Textarea = React.forwardRef((props, ref: any) => (
  <Input {...props} as="textarea" ref={ref} />
));
const nextStateMap: object = {
  1: 'Next',
  2: 'Next: Summary',
  3: 'Next: Experience',
  4: 'Next: Education',
  5: 'Next: Skills',
  6: 'Next: Extra Sections',
  7: 'Next: Download CV',
};

const prevStateMap: object = {
  1: 'Cancel',
  2: 'Previous',
  3: 'Previous: Personal Data',
  4: 'Previous: Summary',
  5: 'Previous: Experience',
  6: 'Previous: Education',
  7: 'Previous: Skills',
};

class CVMakerBuilderScreen extends React.Component<any, State> {
  quillRef: any;

  eduQuillRef: Array<any>;

  expQuillRef: Array<any>;

  formCVInfoRef: React.Ref<
    FormInstance<
      Record<string, any>,
      string,
      {
        [x: string]: string | undefined;
      }
    >
  >;

  formPersonalInfoRef: React.Ref<
    FormInstance<
      Record<string, any>,
      string,
      {
        [x: string]: string | undefined;
      }
    >
  >;

  formProfessionalInfoRef: React.Ref<
    FormInstance<
      Record<string, any>,
      string,
      {
        [x: string]: string | undefined;
      }
    >
  >;

  formCVInfoModel: any;

  formPersonalInfoModel: any;

  formExpInfoModel: any;

  formProfessionalInfoModel: any;

  formEduInfoModel: any;

  constructor(props: any) {
    super(props);

    this.formCVInfoRef = React.createRef();
    this.formPersonalInfoRef = React.createRef();
    this.formProfessionalInfoRef = React.createRef();
    this.quillRef = React.createRef();
    this.eduQuillRef = [];
    this.expQuillRef = [];

    this.formCVInfoModel = Schema.Model({
      name: StringType()
        .addRule(
          (value) => typeof value === 'string' && /\.pdf$/i.test(value.trim()),
          'File name must end with .pdf',
        )
        .isRequired('Please fill the required field!'),
      title: StringType().isRequired('Please fill the required field!'),
      description: StringType(),
      experience: StringType().isRequired('Please choose the required field!'),
    });
    this.formPersonalInfoModel = Schema.Model({
      fullName: StringType().isRequired('Please fill the required field!'),
      jobTitle: StringType().isRequired('Please fill the required field!'),
      city: StringType().isRequired('Please choose the required field!'),
      country: StringType().isRequired('Please choose the required field!'),
      email: StringType()
        .isEmail('Invalid email address format!')
        .isRequired('Please fill the required field!'),
      phoneRegion: StringType().isRequired('Please fill the required field'),
      phoneNumber: StringType()
        .pattern(
          constant.regex.phoneNumber,
          'Your phone number pattern is not correct',
        )
        .isRequired('Please fil the required field!'),
      linkedInProfile: StringType()
        .pattern(
          constant.regex.linkedInProfile,
          'Please enter a valid LinkedIn Profile URL',
        )
        .isRequired('Please fill the required field!'),
      additionalLinks: ArrayType(),
    });
    this.formExpInfoModel = Schema.Model({
      data: ArrayType()
        .of(
          ObjectType()
            .shape({
              jobType: StringType().isRequired(
                'Please fill the required field!',
              ),
              jobTitle: StringType().isRequired(
                'Please fill the required field!',
              ),
              employer: StringType().isRequired(
                'Please fill the required field!',
              ),
              location: StringType().isRequired(
                'Please choose the required field!',
              ),
              jobDesc: StringType().isRequired(
                'Please choose the required field!',
              ),
              startDate: StringType().isRequired(
                'Please choose the required field!',
              ),
              endDate: StringType().isRequired(
                'Please choose the required field!',
              ),
            })
            .isRequired(),
        )
        .isRequired()
        .minLength(1),
    });
    this.formEduInfoModel = Schema.Model({
      data: ArrayType()
        .of(
          ObjectType()
            .shape({
              universityName: StringType().isRequired(
                'Please fill the required field!',
              ),
              degree: StringType().isRequired(
                'Please fill the required field!',
              ),
              major: StringType().isRequired(
                'Please choose the required field!',
              ),
              location: StringType().isRequired(
                'Please choose the required field!',
              ),
              achievements: StringType().isRequired(
                'Please choose the required field!',
              ),
              startDate: StringType().isRequired(
                'Please choose the required field!',
              ),
              endDate: StringType().isRequired(
                'Please choose the required field!',
              ),
            })
            .isRequired(),
        )
        .isRequired()
        .minLength(1),
    });
    this.formProfessionalInfoModel = Schema.Model({
      summary: StringType()
        .isRequired('Please fill the required field!')
        .minLength(255),
    });

    this.state = {
      payload: {
        personalData: {
          fullName: '',
          phoneRegion: '+62',
        },
      },
      recommendations: {},
      isLoading: false,
      loadingSummary: false,
      loadingSkills: false,
      step: 1,
      countries: [],
      cities: [],
      jobTypes: [],
      phoneCodes: [],
      universities: [],
      degrees: [],
      majors: [],
      toggleCVPreview: false,
      toggleEdu: [false],
      toggleExperience: [false],
    };

    // Get default values from AuthStorage
    const storage = AuthStorage.getInstance();
    if (storage.isAuthenticated()) {
      const user = storage.getUser();
      const currPayload = _.get(this, 'state.payload', {});

      const linkedIn = _.get(user, 'profile.linkedinUrl');
      if (linkedIn) {
        _.set(currPayload, 'personalData.linkedInProfile', linkedIn);
      }

      const fullName = _.get(user, 'name');
      if (fullName) {
        _.set(currPayload, 'personalData.fullName', fullName);
      }

      const phoneNumber = String(_.get(user, 'phone')).replace('+62', '');
      if (phoneNumber) {
        _.set(currPayload, 'personalData.phoneNumber', phoneNumber);
      }

      const email = _.get(user, 'email');
      if (email) {
        _.set(currPayload, 'personalData.email', email);
      }

      this.state = {
        ...this.state,
        payload: currPayload,
      };
    }

    const { location } = this.props;
    const { state } = location;
    const cvData = _.get(state, 'cvData');
    const personalizedSummary = _.get(state, 'personalizedSummary');

    if (cvData) {
      const jobTitle =
        _.get(cvData, 'workExperiences.length') > 0
          ? _.get(
              cvData,
              `workExperiences[${cvData.workExperiences.length - 1}].role`,
            )
          : '';
      const yoeInParts = _.split(_.get(cvData, 'yoE'), ' ');
      const yoeInMth = yoeInParts[1];
      const yoeInYr = yoeInParts[0];

      const years = parseInt(yoeInYr, 10);
      const months = parseInt(yoeInMth, 10);
      const totalMonths = years * 12 + months;
      const today = moment().format('DD/MM/YYYY');

      let experience = constant.forms.experience.levels[3].label;
      if (totalMonths === 0) {
        experience = constant.forms.experience.levels[0].label;
      } else if (totalMonths >= 6 && totalMonths < 36) {
        experience = constant.forms.experience.levels[1].label;
      } else if (totalMonths >= 36 && totalMonths < 64) {
        experience = constant.forms.experience.levels[2].label;
      }

      this.state = {
        ...this.state,
        isLoading: true,
        payload: {
          educationData: {
            data: _.map(_.get(cvData, 'educations'), (d: any) => ({
              universityName: _.get(d, 'institution'),
              location: _.get(d, 'location'),
              startDate: _.get(d, 'startDate'),
              endDate: _.get(d, 'endDate'),
              degree: _.get(d, 'degree'),
            })),
          },
          professionalData: {
            summary: `<ul><li>${personalizedSummary}</li></ul>`,
          },
          experienceData: {
            data: _.map(_.get(cvData, 'workExperiences'), (d: any) => ({
              jobTitle: _.get(d, 'role'),
              startDate: _.get(d, 'startDate'),
              endDate: _.get(d, 'endDate'),
              jobDesc: `<ul>${_.join(
                _.map(_.get(d, 'description'), (o) => `<li>${o}</li>`),
                '',
              )}</ul>`,
              jobType: _.get(d, 'jobType'),
              employer: _.get(d, 'company'),
              location:
                _.get(d, 'location') ||
                _.get(cvData, 'location') ||
                _.get(cvData, 'educations[0].location'),
            })),
          },
          skillData: {
            data: _.get(cvData, 'skills', []),
          },
          personalData: {
            fullName: _.get(cvData, 'name'),
            jobTitle: _.get(cvData, 'currentTitle'),
            phoneRegion: _.get(cvData, 'contactNumberRegion'),
            phoneNumber: _.get(cvData, 'contactNumber'),
            country: _.get(cvData, 'country') || '',
            city: _.get(cvData, 'city') || '',
            email: _.get(cvData, 'email'),
            linkedInProfile: _.get(cvData, 'linkedinUrl'),
            additionalLinks: [''],
          },
          name: String(_.get(cvData, 'fileName')).replace('temp_', ''),
          title: jobTitle,
          experience,
        },
        toggleExperience: _.map(
          _.get(cvData, 'workExperiences'),
          (v) => v.endDate === today,
        ),
        toggleEdu: _.map(
          _.get(cvData, 'educations'),
          (v) => v.endDate === today,
        ),
      };
    } else {
      // Get state from session
      const sessionState = SessionStorage.loadState();
      if (!_.isEmpty(sessionState)) {
        this.state = sessionState;
      }
    }
  }

  async componentDidMount(): Promise<void> {
    // eslint-disable-next-line global-require
    require('./styles.scss');

    const promises = [
      new Promise((resolve, reject) => {
        try {
          this.getPhoneCodes();
          resolve();
        } catch (err) {
          reject(err);
        }
      }),

      new Promise((resolve, reject) => {
        try {
          this.getCountries();
          resolve();
        } catch (err) {
          reject(err);
        }
      }),

      new Promise((resolve, reject) => {
        try {
          this.getJobTypes();
          resolve();
        } catch (err) {
          reject(err);
        }
      }),

      new Promise((resolve, reject) => {
        try {
          const { payload } = this.state;
          const university = _.get(
            payload,
            'educationData.data.[0].universityName',
          );
          this.getUniversities(university);
          resolve();
        } catch (err) {
          reject(err);
        }
      }),

      new Promise((resolve, reject) => {
        try {
          this.getMajors();
          resolve();
        } catch (err) {
          reject(err);
        }
      }),

      new Promise((resolve, reject) => {
        try {
          this.getDegree();
          resolve();
        } catch (err) {
          reject(err);
        }
      }),

      new Promise((resolve, reject) => {
        try {
          this.getRecommSkills();
          resolve();
        } catch (err) {
          reject(err);
        }
      }),

      new Promise((resolve, reject) => {
        try {
          this.getDefaultPromo();
          resolve();
        } catch (err) {
          reject(err);
        }
      }),

      new Promise((resolve) => {
        setTimeout(() => {
          resolve();
          this.setState({ isLoading: false });
        }, 500);
      }),
    ];

    const { payload } = this.state;
    const personalData = _.get(payload, 'personalData');
    if (!_.isEmpty(personalData?.country)) {
      promises.push(
        new Promise((resolve, reject) => {
          try {
            this.getCity(personalData?.country || '');
            resolve();
          } catch (err) {
            reject(err);
          }
        }),
      );
    }

    await Promise.allSettled(promises);

    const { location } = this.props;
    if (location.state && location.state.cvData) {
      // Overwrite the current history entry with the current URL
      // This removes PageA from the stack
      window.history.replaceState(null, '', window.location.href);
    }

    // Disable copy and paste in editor
    const quill = this.quillRef.root;
    if (!quill) return;

    quill.addEventListener('copy', (e: any): void => e.preventDefault());
    quill.addEventListener('cut', (e: any): void => e.preventDefault());
  }

  componentDidUpdate(
    _prevProps: Readonly<any>,
    prevState: Readonly<State>,
  ): void {
    const { step } = this.state;
    const { location } = this.props;

    if (step !== prevState.step) {
      window.scrollTo(0, 0);

      if (step > 1) {
        EventTrackerAPI.pushEvent(`${location.pathname}/step/${step - 1}`);
      }
    }
  }

  componentWillUnmount(): void {
    if (this.quillRef) {
      const quill = this.quillRef.root;
      if (!quill) return;

      quill.removeEventListener('copy', (e: any): void => e.preventDefault());
      quill.removeEventListener('cut', (e: any): void => e.preventDefault());
    }
  }

  private onToggleDisabledExp = (idx: number): void => {
    const toggle: Array<boolean> = _.get(this, 'state.toggleExperience') || [];
    const data: object =
      _.get(this, 'state.payload.experienceData.data', []) || [];

    if (idx < toggle.length) {
      const now = moment().format('DD/MM/YYYY');
      toggle[idx] = !toggle[idx];

      if (toggle[idx]) {
        _.set(data, `${idx}.endDate`, now);
      } else {
        _.set(data, `${idx}.endDate`, '');
      }

      this.setState({
        toggleExperience: toggle,
        payload: {
          ..._.get(this, 'state.payload'),
          experienceData: {
            data,
          },
        },
      });
    }
  };

  private onToggleDisabledEdu = (idx: number): void => {
    const toggle: Array<boolean> = _.get(this, 'state.toggleEdu') || [];
    const data: Array<EducationData> =
      _.get(this, 'state.payload.educationData.data', []) || [];

    if (idx < toggle.length) {
      const now = moment().format('DD/MM/YYYY');
      toggle[idx] = !toggle[idx];

      if (toggle[idx]) {
        _.set(data, `${idx}.endDate`, now);
      } else {
        _.set(data, `${idx}.endDate`, '');
      }

      this.setState({
        toggleEdu: toggle,
        payload: {
          ..._.get(this, 'state.payload'),
          educationData: {
            data,
          },
        },
      });
    }
  };

  private onUpdateExtraSection(
    e: React.SyntheticEvent<Element, Event> | undefined,
    id: number,
    type: 'certifications' | 'languages' | 'projects' | 'volunteers',
    key: string,
  ): void {
    const payload = _.get(this.state, 'payload');
    const extraSection = _.get(payload, 'extraSectionData') || {};
    const value = _.get(e, 'target.value');

    switch (type) {
      case 'certifications':
        const certifications: CertificationData[] = _.get(
          extraSection,
          type,
          [],
        );
        if (id < certifications?.length) {
          _.set(certifications, `[${id}].${key}`, value);
        }

        _.set(extraSection, type, certifications);
        break;
      case 'languages':
        const languages: LanguageData[] = _.get(extraSection, type, []);
        if (id < languages?.length) {
          _.set(languages, `[${id}].${key}`, value);
        }

        _.set(extraSection, type, languages);
        break;

      case 'projects':
        const projects: ProjectData[] = _.get(extraSection, type, []);
        if (id < projects?.length) {
          _.set(projects, `[${id}].${key}`, value);
        }

        _.set(extraSection, type, projects);
        break;
      case 'volunteers':
        const volunteers: VolunteerData[] = _.get(extraSection, type, []);
        if (id < volunteers?.length) {
          _.set(volunteers, `[${id}].${key}`, value);
        }

        _.set(extraSection, type, volunteers);
        break;
      default:
        break;
    }

    this.setState({
      payload: {
        ...payload,
        extraSectionData: extraSection,
      },
    });
  }

  private getDefaultPromo = async (): Promise<void> => {
    try {
      const response = await OrderAPI.getDefaultActivePromo();
      const promo = _.get(response, 'data');

      this.setState({ promo });
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  private onDeleteExtra = (
    idx: number,
    type: 'certificate' | 'language' | 'project' | 'volunteer',
  ): void => {
    const payload = _.get(this.state, 'payload');
    const extraSection = _.get(payload, 'extraSectionData') || {};

    switch (type) {
      case 'certificate':
        if (extraSection) {
          const certifications: CertificationData[] =
            _.get(extraSection, 'certifications') || [];
          certifications.splice(idx, 1);

          _.set(extraSection, 'certifications', certifications);
        }
        break;
      case 'language':
        if (extraSection) {
          const languages: LanguageData[] =
            _.get(extraSection, 'languages') || [];
          languages.splice(idx, 1);

          _.set(extraSection, 'languages', languages);
        }
        break;
      case 'project':
        if (extraSection) {
          const projects: ProjectData[] = _.get(extraSection, 'projects') || [];
          projects.splice(idx, 1);

          _.set(extraSection, 'projects', projects);
        }
        break;
      default:
        if (extraSection) {
          const volunteers: VolunteerData[] =
            _.get(extraSection, 'volunteers') || [];
          volunteers.splice(idx, 1);

          _.set(extraSection, 'volunteers', volunteers);
        }
        break;
    }

    this.setState({
      payload: {
        ...payload,
        extraSectionData: extraSection,
      },
    });
  };

  private getMajors = async (query = ''): Promise<void> => {
    try {
      const response = await MiscAPI.getMajors(query);
      const data = _.get(response, 'data') || [];
      this.setState({ majors: data });
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    }
  };

  private getUniversities = async (query = ''): Promise<void> => {
    try {
      const response = await MiscAPI.getUniversities(query);
      const data = _.get(response, 'data') || [];
      this.setState({ universities: data });
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    }
  };

  private getDegree = async (): Promise<void> => {
    try {
      const response = await MiscAPI.getDegrees();
      const data = _.get(response, 'data') || [];
      this.setState({ degrees: data });
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    }
  };

  private getPhoneCodes = async (): Promise<void> => {
    const r = await MiscAPI.getPhoneCode();
    const data = _.get(r, 'data');
    this.setState({
      phoneCodes: _.uniqBy(data, 'dialCode'),
    });
  };

  private getCountries = async (): Promise<void> => {
    try {
      const response = await MiscAPI.getCountriesWithFlag();
      const data = _.get(response, 'data') || [];
      this.setState({ countries: data });
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    }
  };

  private getCity = async (country: string): Promise<void> => {
    try {
      const response = await MiscAPI.getCity(country);
      const data = _.get(response, 'data') || [];
      this.setState({ cities: data });
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    }
  };

  private getRecommWorkProfile = async (): Promise<void> => {
    try {
      this.setState({
        loadingSummary: true,
        recommendations: { workProfile: [] },
      });

      const recommendations = _.get(this.state, 'recommendations');
      const payload = _.get(this.state, 'payload');
      const personalData = _.get(payload, 'personalData');
      const professionalData = _.get(payload, 'professionalData');

      const req = {};
      if (personalData?.fullName) {
        _.set(req, 'name', personalData.fullName);
      } else {
        throw new Error(
          'Please fill your full name, and the AI will assist you in rephrasing it.',
        );
      }

      if (!_.isEmpty(_.get(payload, 'title'))) {
        _.set(req, 'targetRole', _.get(payload, 'title'));
      } else {
        throw new Error(
          'Please fill your target role, and the AI will assist you in rephrasing it.',
        );
      }

      if (professionalData?.summary) {
        _.set(req, 'description', this.quillRef.getText());
      } else {
        throw new Error(
          'Please complete the summary, and the AI will assist you in rephrasing it.',
        );
      }

      if (personalData?.jobTitle) {
        _.set(req, 'role', personalData.jobTitle);
      } else {
        _.set(req, 'role', _.get(payload, 'title'));
      }

      const response = await CVMakerAPI.recommWorkProfile(req);
      const data: Array<string> =
        _.get(response, 'data.data.recommendations') || [];

      this.setState({
        recommendations: {
          ...recommendations,
          workProfile: data,
        },
      });
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    } finally {
      this.setState({ loadingSummary: false });
    }
  };

  private getRecommWorkExp = async (idx: number): Promise<void> => {
    try {
      const experienceData: Array<any> =
        _.get(this, 'state.payload.experienceData.data', []) || [];
      const payload = {
        currentRole: _.get(experienceData, `${idx}.jobTitle`) || '',
        currentRoleType: _.get(experienceData, `${idx}.jobType`) || '',
        description: _.get(experienceData, `${idx}.description`) || '-',
      };

      if (
        _.isEmpty(_.get(payload, 'currentRole')) ||
        _.isEmpty(_.get(payload, 'currentRoleType'))
      ) {
        return;
      }

      this.setState({
        loadingExp: {
          [idx]: true,
        },
      });

      const recommendations = _.get(this.state, 'recommendations', {});
      const response = await CVMakerAPI.recommWorkExp(payload);
      const data: Array<string> =
        _.get(response, 'data.data.recommendations') || [];

      this.setState({
        recommendations: {
          ...recommendations,
          workExperience: {
            ..._.get(this, 'state.recommendations.workExperience'),
            [idx]: data,
          },
        },
      });
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    } finally {
      this.setState({
        loadingExp: {
          [idx]: false,
        },
      });
    }
  };

  private getRecommEdu = async (idx: number): Promise<void> => {
    try {
      try {
        const educations: Array<any> =
          _.get(this, 'state.payload.educationData.data', []) || [];
        const payload = {
          currentMajor: _.get(educations, `${idx}.major`) || '',
        };

        if (_.isEmpty(_.get(payload, 'currentMajor'))) {
          return;
        }

        this.setState({
          loadingEdu: {
            [idx]: true,
          },
        });

        const recommendations = _.get(this.state, 'recommendations', {});
        const response = await CVMakerAPI.recommEducations(payload);
        const data: Array<string> =
          _.get(response, 'data.data.recommendations') || [];

        this.setState({
          recommendations: {
            ...recommendations,
            educations: {
              ..._.get(this, 'state.recommendations.educations'),
              [idx]: data,
            },
          },
        });
      } catch (e: any) {
        toaster.push(util.buildErrorMessage(e.message));
      } finally {
        this.setState({
          loadingEdu: {
            [idx]: false,
          },
        });
      }
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    }
  };

  private getRecommSkills = async (): Promise<void> => {
    try {
      this.setState({
        loadingSkills: true,
        recommendations: { skills: {} },
      });

      const payload = _.get(this.state, 'payload');
      const personalData = _.get(payload, 'personalData');
      const experienceData = _.get(payload, 'experienceData');
      const recommendations = _.get(this.state, 'recommendations', {});

      let title = personalData?.jobTitle;
      if (_.get(experienceData, 'data.length', 0) > 0) {
        title =
          experienceData?.data[_.get(experienceData, 'data.length') - 1]
            .jobTitle;
      }

      if (_.isEmpty(title)) {
        return;
      }

      const response = await CVMakerAPI.recommSkills({
        currentRole: title,
      });
      const data: object = _.get(response, 'data.data') || {};

      this.setState({
        recommendations: {
          ...recommendations,
          skills: data,
        },
      });
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    } finally {
      this.setState({
        loadingSkills: false,
      });
    }
  };

  private getJobTypes = async (): Promise<void> => {
    const response = await MiscAPI.getAttributes('EMPLOYMENT_STATUS');
    const data: any[] = _.get(response, 'data') || [];

    data.push({ name: 'Self Project' });
    data.push({ name: 'Volunteer' });

    this.setState({
      jobTypes: data,
    });
  };

  private onFormValueChanged = (value: any, key: string): void =>
    this.setState((prevState) => util.setNested(prevState, key, value));

  private onAddWorkProfileSummary = (value: string): void => {
    if (this.quillRef) {
      const lastIndex = this.quillRef.getLength() - 1;
      this.quillRef.insertText(lastIndex, `${value}\n`, 'list');
      this.quillRef.formatLine(lastIndex, 1, 'list', 'bullet');
    }
  };

  private onAddExpJobDescRecom = (idx: number, value: string): void => {
    if (idx < this.expQuillRef.length) {
      const textEditor = this.expQuillRef[idx];
      const lastIndex = textEditor.getLength() - 1;

      textEditor.root?.setAttribute('spellcheck', false);
      textEditor.insertText(lastIndex, `${value}\n`, 'list');
      textEditor.formatLine(lastIndex, 1, 'list', 'bullet');
    }
  };

  private onAddEducationAchievementRecom = (
    idx: number,
    value: string,
  ): void => {
    if (idx < this.eduQuillRef.length) {
      const textEditor = this.eduQuillRef[idx];
      const lastIndex = textEditor.getLength() - 1;

      textEditor.root?.setAttribute('spellcheck', false);
      textEditor.insertText(lastIndex, `${value}\n`, 'list');
      textEditor.formatLine(lastIndex, 1, 'list', 'bullet');
    }
  };

  private onAddNewExtraSection = (
    type: 'certificate' | 'language' | 'project' | 'volunteer',
  ): void => {
    const payload = _.get(this.state, 'payload');
    const extraSection = _.get(payload, 'extraSectionData') || {};

    switch (type) {
      case 'certificate':
        if (extraSection) {
          const certifications: CertificationData[] =
            _.get(extraSection, 'certifications') || [];
          certifications.push({
            name: '',
            issuer: '',
            year: 0,
          });

          _.set(extraSection, 'certifications', certifications);
        }
        break;
      case 'language':
        if (extraSection) {
          const languages: LanguageData[] =
            _.get(extraSection, 'languages') || [];
          languages.push({
            language: '',
            proficient: '',
          });

          _.set(extraSection, 'languages', languages);
        }
        break;
      case 'project':
        if (extraSection) {
          const projects: ProjectData[] = _.get(extraSection, 'projects') || [];
          projects.push({
            name: '',
            year: 0,
            role: '',
            contribution: '',
          });

          _.set(extraSection, 'projects', projects);
        }
        break;
      default:
        if (extraSection) {
          const volunteers: VolunteerData[] =
            _.get(extraSection, 'volunteers') || [];
          volunteers.push({
            name: '',
            duration: '',
            role: '',
            contribution: '',
          });

          _.set(extraSection, 'volunteers', volunteers);
        }
        break;
    }

    this.setState({
      payload: {
        ...payload,
        extraSectionData: extraSection,
      },
    });
  };

  private onContinueClicked = (): void => {
    try {
      const { t, location } = this.props;
      const { step, payload: cvPayload } = this.state;

      switch (step) {
        case 1:
          if (!_.invoke(this.formCVInfoRef, 'current.check')) {
            throw new Error(
              t(constant.translation.basicInfo.formErrorValidationMessageKey),
            );
          }
          break;
        case 2:
          if (!_.invoke(this.formPersonalInfoRef, 'current.check')) {
            throw new Error(
              t(constant.translation.basicInfo.formErrorValidationMessageKey),
            );
          }
          break;
        case 3:
          if (this.quillRef.getLength() <= 1) {
            throw new Error(
              t(constant.translation.basicInfo.formErrorValidationMessageKey),
            );
          }
          break;
        case 4:
          if (this.formExpInfoModel) {
            const payload = _.get(this, 'state.payload.experienceData');
            const checkErr = this.formExpInfoModel.check(payload);

            if (_.isEmpty(checkErr)) {
              throw new Error(
                t(constant.translation.basicInfo.formErrorValidationMessageKey),
              );
            }

            if (checkErr && checkErr?.data?.hasError) {
              throw new Error(
                t(constant.translation.basicInfo.formErrorValidationMessageKey),
              );
            }
          }

          break;
        case 5:
          if (this.formEduInfoModel) {
            this.getRecommSkills();

            const payload = _.get(this, 'state.payload.educationData');
            const checkErr = this.formEduInfoModel.check(payload);

            if (_.isEmpty(checkErr)) {
              throw new Error(
                t(constant.translation.basicInfo.formErrorValidationMessageKey),
              );
            }

            if (checkErr && checkErr?.data?.hasError) {
              throw new Error(
                t(constant.translation.basicInfo.formErrorValidationMessageKey),
              );
            }
          }

          break;
        case 7: {
          const { navigate } = this.props;

          // Store the state in the session storage
          SessionStorage.saveState(
            this.state,
            SessionStorage.PageKey.CvMakerBuild,
          );

          navigate('/cv-maker/payment', {
            state: {
              from: location?.pathname,
              payload: cvPayload,
            },
          });

          return;
        }
        default:
          break;
      }

      this.setState({ step: step + 1 });
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    }
  };

  private onRemoveExp = (e: any, idx: number): void => {
    e.preventDefault();

    const { state } = this;

    const toggle = _.get(state, 'payload.toggleExperience') || [];
    const data = _.get(state, 'payload.experienceData.data') || [];

    data.splice(idx, 1);
    this.setState({
      payload: {
        ..._.get(state, 'payload'),
        experienceData: {
          data,
        },
      },
      toggleExperience: toggle,
    });
  };

  private onRemoveEdu = (e: any, idx: number): void => {
    e.preventDefault();

    const { state } = this;
    const toggle = _.get(state, 'payload.toggleEdu') || [];
    const data = _.get(state, 'payload.educationData.data') || [];

    data.splice(idx, 1);
    toggle.splice(idx, 1);

    this.setState({
      payload: {
        ..._.get(state, 'payload'),
        educationData: {
          data,
        },
      },
      toggleEdu: toggle,
    });
  };

  private onCancelClicked = (): void => {
    const { step } = this.state;
    if (step === 1) {
      const { navigate } = this.props;
      navigate(-1);
    } else {
      this.setState({ step: step - 1 });
    }
  };

  private onAddMoreExperience = (): void => {
    const experienceData: Array<any> =
      _.get(this, 'state.payload.experienceData.data', []) || [];
    const toggle: Array<boolean> = _.get(this, 'state.toggleExperience') || [];

    experienceData.push({});
    toggle.push(false);

    this.expQuillRef.push(React.createRef());

    this.setState({
      payload: {
        ..._.get(this, 'state.payload'),
        experienceData: {
          data: experienceData,
        },
      },
      toggleExperience: toggle,
    });
  };

  private onAddMoreEducation = (): void => {
    const educationData: Array<any> =
      _.get(this, 'state.payload.educationData.data', []) || [];
    const toggle: Array<boolean> = _.get(this, 'state.toggleEdu') || [];

    educationData.push({});
    toggle.push(false);

    this.eduQuillRef.push(React.createRef());

    this.setState({
      payload: {
        ..._.get(this, 'state.payload'),
        educationData: {
          data: educationData,
        },
      },
    });
  };

  private onDeleteSkill = (idx: number): void => {
    const skillData = _.get(this, 'state.payload.skillData');
    const data = _.get(skillData, 'data') as Array<string>;
    const deletedData = data[idx];
    const currState = {
      skillName: _.get(skillData, 'skillName'),
      data: data.filter((_value, _idx) => _idx !== idx),
    };

    this.setState(
      {
        payload: {
          ..._.get(this, 'state.payload'),
          skillData: {},
        },
      },
      () => {
        this.setState({
          payload: {
            ..._.get(this, 'state.payload'),
            skillData: currState,
          },
        });

        toaster.push(
          util.buildInfoMessage(`Successfully delete "${deletedData}" skill!`),
        );
      },
    );
  };

  private onAddNewSkill = (skill?: string): void => {
    const skillData = _.get(this, 'state.payload.skillData');
    const data: any[] = _.get(skillData, 'data', []) || [];
    const newSkill = skill || _.get(skillData, 'skillName', null) || '';

    if (data.length >= 5) {
      toaster.push(util.buildErrorMessage(`You can add up to 5 skills only!`));
      return;
    }

    if (_.find(data, (v) => _.toLower(v) === _.toLower(newSkill))) {
      toaster.push(util.buildErrorMessage(`${newSkill} is already exists!`));
      return;
    }

    if (!newSkill) {
      toaster.push(
        util.buildErrorMessage('Please fill the required field first!'),
      );
      return;
    }

    data.push(newSkill);

    this.setState({
      payload: {
        ..._.get(this, 'state.payload'),
        skillData: {
          data,
          skillName: '',
        },
      },
    });
  };

  private onAddNewAdditionalLink = (): void => {
    const { payload } = this.state;
    const links = _.get(payload, 'personalData.additionalLinks') || [''];

    links?.push('');

    this.setState({
      payload: {
        ...payload,
        personalData: {
          ..._.get(payload, 'personalData'),
          additionalLinks: links,
        },
      },
    });
  };

  private onRemoveAdditionalLink = (i: number): void => {
    const { payload } = this.state;
    const links = _.get(payload, 'personalData.additionalLinks');

    this.setState({
      payload: {
        ...payload,
        personalData: {
          ..._.get(payload, 'personalData'),
          additionalLinks: links?.filter((l, id) => id !== i),
        },
      },
    });
  };

  renderModal = (): React.ReactNode => {
    const { step } = this.state;
    const { t } = this.props;

    return (
      <Modal
        size="md"
        overflow={false}
        backdrop="static"
        open={step === 1}
        className="cv-maker-builder-modal"
        onClose={() => null}
      >
        <Modal.Header closeButton />
        <Modal.Body>
          <Form
            key="cvinfo-form"
            ref={this.formCVInfoRef}
            model={this.formCVInfoModel}
            onChange={(v: object) => this.onFormValueChanged(v, 'payload')}
            formDefaultValue={_.get(this.state, 'payload')}
            fluid
          >
            <FlexboxGrid justify="center">
              <FlexboxGrid.Item as={Col} xs={24} md={12}>
                <Form.Group>
                  <Form.ControlLabel>
                    {t(constant.translation.cvMaker.formCVName)}
                  </Form.ControlLabel>
                  <Form.Control
                    name="name"
                    placeholder="Enter your CV name (e.g. MyResume.pdf)"
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item as={Col} xs={24} md={12}>
                <Form.Group>
                  <Form.ControlLabel>
                    {t(constant.translation.cvMaker.formJobTitle)}
                  </Form.ControlLabel>
                  <Form.Control
                    name="title"
                    className="text-transform-capitalize"
                    placeholder="Enter your target role"
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item as={Col} xs={24}>
                <Form.Group>
                  <Form.ControlLabel>
                    {t(constant.translation.cvMaker.formJobDesc)}
                  </Form.ControlLabel>
                  <Form.Control
                    name="description"
                    accepter={Textarea as React.ElementType}
                    placeholder={t(
                      constant.translation.cvMaker.formJobDescAIPrompt,
                    )}
                    value={_.get(this.state, 'payload.description')}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item as={Col} xs={24}>
                <Form.Group>
                  <Form.ControlLabel>
                    {t(constant.translation.cvMaker.formExp)}
                  </Form.ControlLabel>
                  <Form.Control name="experience" accepter={RadioGroup}>
                    {constant.forms.experience.levels.map((e) => (
                      <Radio value={e.label}>{e.label}</Radio>
                    ))}
                  </Form.Control>
                </Form.Group>
              </FlexboxGrid.Item>
              <Stack
                spacing={16}
                className="w-100"
                justifyContent="flex-end"
                direction="row"
              >
                <Stack.Item>
                  <Button
                    appearance="subtle"
                    className="btn-tg-fortier"
                    onClick={this.onCancelClicked}
                  >
                    {t(constant.translation.general.formCancelButtonKey)}
                  </Button>
                </Stack.Item>
                <Stack.Item>
                  <Button
                    className="next-btn"
                    appearance="primary"
                    onClick={this.onContinueClicked}
                  >
                    {t(constant.translation.general.formContinueButtonKey)}
                    <Icon as={FaArrowRight} />
                  </Button>
                </Stack.Item>
              </Stack>
            </FlexboxGrid>
          </Form>
        </Modal.Body>
      </Modal>
    );
  };

  renderPersonalDataSection = (): React.ReactNode => {
    const { t } = this.props;
    const { payload } = this.state;

    const additionalLinks = (_.get(
      payload,
      'personalData.additionalLinks',
    ) as Array<string>) || [''];
    const toggleCVPreview = _.get(this, 'state.toggleCVPreview');

    return (
      <div>
        <Form
          key="personaldata-form"
          ref={this.formPersonalInfoRef}
          model={this.formPersonalInfoModel}
          onChange={(v: object) => {
            const links: string[] = [];

            Object.keys(v).forEach((key: string): void => {
              if (key.includes('additionalLinks[')) {
                links.push(_.get(v, key));
                v = _.omit(v, key);
              }
            });

            _.set(v, 'additionalLinks', links);
            this.onFormValueChanged(v, 'payload.personalData');
          }}
          formDefaultValue={_.get(this.state, 'payload.personalData')}
          fluid
        >
          <FlexboxGrid justify="start" align="top">
            <FlexboxGrid.Item as={Col} xs={24}>
              <HeadingGroup className="cvmaker-title-header">
                <Heading>
                  <img src="/icon-personal-data.png" alt="icon-personal-data" />
                  {t(constant.translation.cvMaker.personalInfoTitle)}

                  <Button
                    appearance="link"
                    className="float-right"
                    onClick={() =>
                      this.setState({ toggleCVPreview: !toggleCVPreview })
                    }
                  >
                    <FaEye /> See{' '}
                    {t(constant.translation.cvMaker.cvPreviewTitle)}
                  </Button>
                </Heading>
                <Text muted>
                  {t(constant.translation.cvMaker.personalInfoSubtitle)}
                </Text>
              </HeadingGroup>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item as={Col} xs={24} md={12}>
              <Form.Group>
                <Form.ControlLabel>
                  {t(constant.translation.cvMaker.formFullName)}
                </Form.ControlLabel>
                <Form.Control
                  name="fullName"
                  className="text-transform-capitalize"
                  placeholder="Enter your full name"
                />
              </Form.Group>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item as={Col} xs={24} md={12}>
              <Form.Group>
                <Form.ControlLabel>
                  {t(constant.translation.cvMaker.formJobTitle)}
                </Form.ControlLabel>
                <Form.Control
                  name="jobTitle"
                  className="text-transform-capitalize"
                  placeholder="Enter your job title"
                />
              </Form.Group>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item as={Col} xs={24} md={12}>
              <Form.Group>
                <Form.ControlLabel>
                  {t(constant.translation.cvMaker.formCountry)}
                </Form.ControlLabel>
                <Form.Control
                  accepter={SelectPicker}
                  name="country"
                  placeholder="Select country"
                  data={_.map(_.get(this, 'state.countries'), (val: any) => ({
                    label: val.name,
                    value: val.name,
                    flag: val.flag,
                  }))}
                  onSelect={(val) => this.getCity(val)}
                  onClean={() => this.setState({ cities: [] })}
                  renderMenuItem={(label, it: ItemDataType) => (
                    <div>
                      <img
                        src={_.get(it, 'flag')}
                        className="mr-4 w-1em img-flag"
                        alt={`flag-country-${it.name}`}
                      />
                      {label}
                    </div>
                  )}
                  className="w-100"
                />
              </Form.Group>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item as={Col} xs={24} md={12}>
              <Form.Group>
                <Form.ControlLabel>
                  {t(constant.translation.cvMaker.formCity)}
                </Form.ControlLabel>
                <Form.Control
                  accepter={SelectPicker}
                  name="city"
                  placeholder="Select city"
                  data={_.map(_.get(this, 'state.cities'), (val: any) => ({
                    label: val,
                    value: val,
                  }))}
                  className="w-100"
                />
              </Form.Group>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item as={Col} xs={24} md={12}>
              <Form.Group>
                <Form.ControlLabel>
                  {t(constant.translation.general.formEmailKey)}
                </Form.ControlLabel>
                <Form.Control
                  name="email"
                  placeholder="Enter your email address"
                />
              </Form.Group>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item as={Col} xs={24} md={12}>
              <Form.Group>
                <Form.ControlLabel>
                  {t(constant.translation.general.formPhoneNumberKey)}
                </Form.ControlLabel>
                <Row>
                  <Col xs={6} sm={4}>
                    <Form.Control
                      name="phoneRegion"
                      placeholder="+62"
                      defaultValue="+62"
                      size="sm"
                      accepter={SelectPicker}
                      appearance="default"
                      data={_.map(_.get(this, 'state.phoneCodes'), (ph) => ({
                        label: _.get(ph, 'dialCode'),
                        value: _.get(ph, 'dialCode'),
                      }))}
                      className="phone-prefix w-100"
                    />
                  </Col>
                  <Col xs={18} sm={20}>
                    <Form.Control
                      name="phoneNumber"
                      placeholder="812345890001"
                    />
                  </Col>
                </Row>
              </Form.Group>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item as={Col} xs={24} sm={16}>
              <Form.Group>
                <Form.ControlLabel>
                  {t(constant.translation.general.formLinkedInKey)}
                </Form.ControlLabel>
                <Form.Control
                  name="linkedInProfile"
                  placeholder="https://linkedin.com/in/john-doe"
                />
              </Form.Group>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item as={Col} xs={24} sm={16}>
              <Form.Group>
                <Form.ControlLabel>
                  {t(constant.translation.cvMaker.formAdditionalLink)}
                </Form.ControlLabel>
                {additionalLinks.length > 0 &&
                  _.map(additionalLinks, (link, i) => (
                    <div className="d-flex">
                      <Form.Control
                        name={`additionalLinks[${i}]`}
                        className="mt-4"
                        placeholder="Enter your additional link"
                        defaultValue={link}
                      />
                      {additionalLinks.length > 1 && (
                        <IconButton
                          onClick={() => this.onRemoveAdditionalLink(i)}
                          appearance="subtle"
                          className="float-right mt-4 ml-4"
                          icon={<FaTrash />}
                        />
                      )}
                    </div>
                  ))}
                <Button
                  appearance="link"
                  className="mt-4"
                  onClick={this.onAddNewAdditionalLink}
                >
                  <FaPlus /> &nbsp; Add More Link
                </Button>
              </Form.Group>
            </FlexboxGrid.Item>
          </FlexboxGrid>
        </Form>
        <br />
      </div>
    );
  };

  renderProfessionalSection = (): React.ReactNode => {
    const { t } = this.props;
    const { state } = this;
    const recommendations = _.get(state, 'recommendations');
    const workProfile: Array<string> = _.get(
      recommendations,
      'workProfile',
      [],
    );

    const toggleCVPreview = _.get(state, 'toggleCVPreview');
    const loadingSummary = _.get(state, 'loadingSummary');

    return (
      <div>
        <Form
          key="professional-form"
          ref={this.formProfessionalInfoRef}
          model={this.formProfessionalInfoModel}
          onChange={(v: object) =>
            this.onFormValueChanged(v, 'payload.professionalData')
          }
          formValue={_.get(this.state, 'payload.professionalData')}
          fluid
        >
          <FlexboxGrid justify="start" align="top">
            <FlexboxGrid.Item as={Col} xs={24} className="mb-4">
              <HeadingGroup className="cvmaker-title-header">
                <Heading>
                  <img src="/icon-summary.png" alt="icon-summary" />
                  {t(constant.translation.cvMaker.dataInfoTitle)}

                  <Button
                    appearance="link"
                    className="float-right"
                    onClick={() =>
                      this.setState({ toggleCVPreview: !toggleCVPreview })
                    }
                  >
                    <FaEye /> See{' '}
                    {t(constant.translation.cvMaker.cvPreviewTitle)}
                  </Button>
                </Heading>
                <Text muted>
                  {t(constant.translation.cvMaker.dataInfoSubtitle)}
                </Text>
              </HeadingGroup>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item as={Col} xs={24} md={12}>
              <Form.Group>
                <Form.ControlLabel>
                  {t(constant.translation.cvMaker.formSummary)}
                </Form.ControlLabel>
                <ReactQuill
                  ref={(e) => {
                    this.quillRef = e?.getEditor();
                  }}
                  theme="snow"
                  placeholder={t(
                    constant.translation.cvMaker.formSummaryPlaceholder,
                  )}
                  value={_.get(this.state, 'payload.professionalData.summary')}
                  onChange={(value: any) => {
                    this.setState({
                      payload: {
                        ..._.get(this, 'state.payload'),
                        professionalData: {
                          summary: value,
                        },
                      },
                    });
                  }}
                />
                <Button
                  appearance="primary"
                  className="tg-ai-generate-btn"
                  onClick={this.getRecommWorkProfile}
                  disabled={
                    loadingSummary ||
                    _.isEmpty(
                      _.get(this, 'state.payload.professionalData.summary'),
                    ) ||
                    _.get(this, 'state.payload.professionalData.summary') ===
                      '<p><br></p>'
                  }
                >
                  {/* sparkle icon */}
                  <span className="tg-ai-sparkle" aria-hidden>
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="currentColor"
                    >
                      <path d="M12 2l1.6 4.4L18 8l-4.4 1.6L12 14l-1.6-4.4L6 8l4.4-1.6L12 2z" />
                      <path d="M19 12l.9 2.4L22 15l-2.1.6L19 18l-.9-2.4L16 15l2.1-.6L19 12z" />
                    </svg>
                  </span>
                  <span className="tg-ai-label">Write with AI</span>
                  {loadingSummary && (
                    <Loader size="sm" className="tg-ai-loader" />
                  )}
                </Button>
              </Form.Group>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item
              as={Col}
              xs={24}
              md={12}
              style={{ marginTop: '2.5rem' }}
            >
              <Panel
                shaded
                bordered
                className="ml-4 ai-work-profile-panel"
                header={
                  <>
                    <Heading>
                      <img src="/icon-summary-ai.png" alt="icon-summary" />
                      {t(constant.translation.cvMaker.cvAIBuilderTitle)}
                    </Heading>
                    <Text muted>
                      {t(constant.translation.cvMaker.cvAIBuilderText)}
                    </Text>
                  </>
                }
              >
                <List
                  bordered
                  divider
                  hover
                  className="w-100 tg-ai-recommendation-list"
                  autoScroll
                >
                  {(workProfile || []).map((recommendation: string) => (
                    <List.Item>
                      <Row className="w-100">
                        <Col xs={22}>{recommendation}</Col>
                        <Col xs={2}>
                          <IconButton
                            circle
                            onClick={() =>
                              this.onAddWorkProfileSummary(recommendation)
                            }
                            icon={<Plus />}
                            appearance="default"
                          />
                        </Col>
                      </Row>
                    </List.Item>
                  ))}

                  {loadingSummary && (
                    <>
                      {[...Array(3)].map(() => (
                        <List.Item>
                          <Placeholder.Paragraph active />
                        </List.Item>
                      ))}
                      <Loader
                        center
                        content={t(
                          constant.translation.general
                            .formGenerateContentLoadingTextKey,
                        )}
                      />
                    </>
                  )}

                  {workProfile.length === 0 && !loadingSummary && (
                    <FlexboxGrid
                      justify="center"
                      className="p-1em pt-2em pb-2em"
                    >
                      <img
                        src="/icon-recomm-not-found.png"
                        alt="icon-not-found"
                      />
                      <Text muted className="mt-8" style={{ fontSize: '1em' }}>
                        This section will populate automatically once you
                        complete the field on the left panel.
                      </Text>
                    </FlexboxGrid>
                  )}
                </List>
              </Panel>
            </FlexboxGrid.Item>
          </FlexboxGrid>
        </Form>
        <br />
      </div>
    );
  };

  renderExperienceSection = (): React.ReactNode => {
    const { t } = this.props;
    const experienceData: Array<any> =
      _.get(this, 'state.payload.experienceData.data', []) || [];

    if (experienceData.length === 0) {
      experienceData.push({});
    }

    const loadingExp = _.get(this, 'state.loadingExp');
    const toggleCVPreview = _.get(this, 'state.toggleCVPreview');

    const experiences: object = _.get(
      this.state,
      'recommendations.workExperience',
      [],
    );

    return (
      <>
        <FlexboxGrid justify="start" align="top">
          <FlexboxGrid.Item as={Col} xs={24} className="mb-2">
            <HeadingGroup className="cvmaker-title-header">
              <Heading>
                <img src="/icon-exp.png" alt="icon-exp" />
                {t(constant.translation.cvMaker.expInfoTitle)}

                <Button
                  appearance="link"
                  className="float-right"
                  onClick={() =>
                    this.setState({ toggleCVPreview: !toggleCVPreview })
                  }
                >
                  <FaEye /> See {t(constant.translation.cvMaker.cvPreviewTitle)}
                </Button>
              </Heading>
              <Text muted>
                {t(constant.translation.cvMaker.expInfoSubtitle)}
              </Text>
            </HeadingGroup>
          </FlexboxGrid.Item>
        </FlexboxGrid>
        <Form
          key="experience-form"
          model={this.formExpInfoModel}
          onChange={(v: object) =>
            this.onFormValueChanged(v, `payload.experienceData`)
          }
          fluid
        >
          {_.map(experienceData, (_data, idx) => (
            <Panel
              className="mt-8"
              header={
                <div>
                  <span>Experience {idx + 1}</span>
                  {experienceData.length > 1 && (
                    <span className="float-right mr-4">
                      <IconButton
                        onClick={(e) => this.onRemoveExp(e, idx)}
                        icon={<FaTrash />}
                        startIcon
                        appearance="default"
                      >
                        Remove
                      </IconButton>
                    </span>
                  )}
                </div>
              }
              defaultExpanded
              collapsible
              bordered
            >
              <FlexboxGrid justify="start" align="middle">
                <FlexboxGrid.Item as={Col} xs={24} md={12}>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t(constant.translation.cvMaker.formJobTitle)}
                    </Form.ControlLabel>
                    <Form.Control
                      name={`data[${idx}].jobTitle`}
                      placeholder="Enter your job title"
                      className="text-transform-capitalize"
                      onBlur={() => this.getRecommWorkExp(idx)}
                      defaultValue={_.get(_data, 'jobTitle')}
                    />
                  </Form.Group>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item as={Col} xs={24} md={12}>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t(constant.translation.cvMaker.formJobType)}
                    </Form.ControlLabel>
                    <Form.Control
                      name={`data[${idx}].jobType`}
                      placeholder="Select job type"
                      className="w-100"
                      searchable={false}
                      accepter={SelectPicker}
                      data={_.map(
                        _.get(this, 'state.jobTypes'),
                        (val: any) => ({
                          label: val.name,
                          value: val.name,
                        }),
                      )}
                      onExit={() => this.getRecommWorkExp(idx)}
                      defaultValue={_.get(_data, 'jobType')}
                    />
                  </Form.Group>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item as={Col} xs={24} md={12}>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t(constant.translation.cvMaker.formEmployer)}
                    </Form.ControlLabel>
                    <Form.Control
                      name={`data[${idx}].employer`}
                      placeholder="Enter your employer name"
                      className="text-transform-capitalize"
                      defaultValue={
                        _.get(_data, 'company') || _.get(_data, 'employer')
                      }
                    />
                  </Form.Group>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item as={Col} xs={24} md={12}>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t(constant.translation.cvMaker.formLocation)}
                    </Form.ControlLabel>
                    <Form.Control
                      accepter={SelectPicker}
                      name={`data[${idx}].location`}
                      placeholder="Select location"
                      data={_.map(
                        _.get(this, 'state.countries'),
                        (val: any) => ({
                          label: val.name,
                          value: val.name,
                          flag: val.flag,
                        }),
                      )}
                      renderMenuItem={(label, it: ItemDataType) => (
                        <div>
                          <img
                            src={_.get(it, 'flag')}
                            className="mr-4 w-1em img-flag"
                            alt={`flag-country-${it.name}`}
                          />
                          {label}
                        </div>
                      )}
                      defaultValue={_.get(_data, 'location')}
                      className="w-100"
                    />
                  </Form.Group>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item as={Col} xs={24} md={12}>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t(constant.translation.cvMaker.formStartDate)}
                    </Form.ControlLabel>
                    <DatePicker
                      name={`data[${idx}].startDate`}
                      onSelect={(value) => {
                        this.onFormValueChanged(
                          moment(value).format('DD/MM/YYYY'),
                          `payload.experienceData.data.${idx}.startDate`,
                        );
                      }}
                      shouldDisableDate={(date) =>
                        util.setPickerDisabledDate(_data, date, 'startDate')
                      }
                      oneTap
                      className="w-100"
                      placement="bottomStart"
                      placeholder="DD MM YYYY"
                      defaultValue={
                        _.get(_data, 'startDate')
                          ? moment(
                              _.get(_data, 'startDate'),
                              'DD/MM/YYYY',
                            ).toDate()
                          : null
                      }
                    />
                  </Form.Group>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item
                  as={Col}
                  xs={24}
                  md={12}
                  style={{ marginTop: '2.7em' }}
                >
                  <Form.Group>
                    <Form.ControlLabel>
                      {t(constant.translation.cvMaker.formEndDate)}
                    </Form.ControlLabel>
                    <DatePicker
                      name={`data[${idx}].endDate`}
                      onSelect={(value) => {
                        this.onFormValueChanged(
                          moment(value).format('DD/MM/YYYY'),
                          `payload.experienceData.data.${idx}.endDate`,
                        );
                      }}
                      shouldDisableDate={(date) =>
                        util.setPickerDisabledDate(_data, date, 'endDate')
                      }
                      oneTap
                      placeholder="DD MM YYYY"
                      placement="bottomStart"
                      className="w-100"
                      defaultValue={
                        _.get(_data, 'endDate')
                          ? moment(
                              _.get(_data, 'endDate'),
                              'DD/MM/YYYY',
                            ).toDate()
                          : null
                      }
                      disabled={_.get(this.state, `toggleExperience.${idx}`)}
                    />
                    <Checkbox
                      defaultChecked={_.get(
                        this.state,
                        `toggleExperience.${idx}`,
                      )}
                      onCheckboxClick={() => this.onToggleDisabledExp(idx)}
                    >
                      {t(
                        constant.translation.cvMaker.endDateCheckboxPlaceholder,
                      )}
                    </Checkbox>
                  </Form.Group>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item as={Col} xs={24} md={12}>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t(constant.translation.cvMaker.formJobDesc)}
                    </Form.ControlLabel>
                    <ReactQuill
                      ref={(e) => {
                        if (this.expQuillRef.length <= idx) {
                          this.expQuillRef.push(React.createRef());
                        }
                        this.expQuillRef[idx] = e?.getEditor();
                      }}
                      theme="snow"
                      value={_.get(
                        this.state,
                        `payload.experienceData.data.${idx}.jobDesc`,
                      )}
                      placeholder={t(
                        constant.translation.cvMaker.formExpPlaceholder,
                      )}
                      onChange={(value: any) => {
                        const currData: Array<any> = _.get(
                          this,
                          'state.payload.experienceData.data',
                          [],
                        );

                        if (currData.length <= idx) {
                          currData.push({});
                        }
                        currData[idx].jobDesc = value;

                        this.setState({
                          payload: {
                            ..._.get(this, 'state.payload'),
                            experienceData: {
                              data: currData,
                            },
                          },
                        });
                      }}
                    />
                  </Form.Group>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item
                  as={Col}
                  xs={24}
                  md={12}
                  style={{ marginTop: '5rem' }}
                >
                  <Panel
                    shaded
                    bordered
                    className="ml-4 ai-work-profile-panel"
                    header={
                      <>
                        <Heading>
                          <img src="/icon-summary-ai.png" alt="icon-summary" />
                          {t(constant.translation.cvMaker.cvAIBuilderTitle)}
                        </Heading>
                        <Text muted>
                          {t(constant.translation.cvMaker.cvAIBuilderText2)}
                        </Text>
                      </>
                    }
                  >
                    <List
                      bordered
                      divider
                      hover
                      className="w-100 tg-ai-recommendation-list"
                      autoScroll
                    >
                      {(_.get(experiences, idx) || []).map(
                        (recommendation: string) => (
                          <List.Item>
                            <Row className="w-100">
                              <Col xs={22}>{recommendation}</Col>
                              <Col xs={2}>
                                <IconButton
                                  circle
                                  icon={<Plus />}
                                  onClick={() =>
                                    this.onAddExpJobDescRecom(
                                      idx,
                                      recommendation,
                                    )
                                  }
                                  appearance="default"
                                />
                              </Col>
                            </Row>
                          </List.Item>
                        ),
                      )}

                      {_.get(loadingExp, idx) && (
                        <>
                          {[...Array(3)].map(() => (
                            <List.Item>
                              <Placeholder.Paragraph active />
                            </List.Item>
                          ))}
                          <Loader
                            center
                            content={t(
                              constant.translation.general
                                .formGenerateContentLoadingTextKey,
                            )}
                          />
                        </>
                      )}

                      {_.get(experiences, `${idx}.length`, 0) === 0 &&
                        !_.get(loadingExp, idx) && (
                          <FlexboxGrid
                            justify="center"
                            as={Col}
                            className="p-1em pt-2em pb-2em"
                          >
                            <FlexboxGrid.Item colspan={24}>
                              <img
                                src="/icon-recomm-not-found.png"
                                alt="icon-not-found"
                                className="d-grid"
                              />
                            </FlexboxGrid.Item>
                            <Text
                              muted
                              className="mt-8"
                              style={{ fontSize: '1em' }}
                            >
                              This section will populate automatically once you
                              complete the field above.
                            </Text>
                          </FlexboxGrid>
                        )}
                    </List>
                  </Panel>
                </FlexboxGrid.Item>
                <br />
              </FlexboxGrid>
            </Panel>
          ))}
        </Form>

        <Button
          appearance="link"
          className="mt-4 m-auto w-100"
          onClick={this.onAddMoreExperience}
        >
          <FaPlus /> &nbsp; Add Experience
        </Button>
      </>
    );
  };

  renderEducationSection = (): React.ReactNode => {
    const { t } = this.props;
    const { universities, majors, degrees } = this.state;
    const educationData: Array<any> =
      _.get(this, 'state.payload.educationData.data', []) || [];

    if (educationData.length === 0) {
      educationData.push({});
    }

    const loadingEdu = _.get(this, 'state.loadingEdu');
    const toggleCVPreview = _.get(this, 'state.toggleCVPreview');

    const educations: object = _.get(
      this.state,
      'recommendations.educations',
      [],
    );

    return (
      <>
        <FlexboxGrid justify="start" align="top">
          <FlexboxGrid.Item as={Col} xs={24} className="mb-2">
            <HeadingGroup className="cvmaker-title-header">
              <Heading>
                <img src="/icon-edu.png" alt="icon-edu" />
                {t(constant.translation.cvMaker.eduInfoTitle)}

                <Button
                  appearance="link"
                  className="float-right"
                  onClick={() =>
                    this.setState({ toggleCVPreview: !toggleCVPreview })
                  }
                >
                  <FaEye /> See {t(constant.translation.cvMaker.cvPreviewTitle)}
                </Button>
              </Heading>
              <Text muted>
                {t(constant.translation.cvMaker.eduInfoSubtitle)}
              </Text>
            </HeadingGroup>
          </FlexboxGrid.Item>
        </FlexboxGrid>
        <Form
          key="education-form"
          model={this.formEduInfoModel}
          onChange={(v: object) =>
            this.onFormValueChanged(v, `payload.educationData`)
          }
          fluid
        >
          {_.map(educationData, (_data, idx) => (
            <Panel
              className="mt-8"
              header={
                <div>
                  <span>Education {idx + 1}</span>
                  {educationData.length > 1 && (
                    <span className="float-right mr-4">
                      <IconButton
                        onClick={(e) => this.onRemoveEdu(e, idx)}
                        icon={<FaTrash />}
                        startIcon
                        appearance="default"
                      >
                        Remove
                      </IconButton>
                    </span>
                  )}
                </div>
              }
              defaultExpanded
              collapsible
              bordered
            >
              <FlexboxGrid justify="start" align="middle">
                <FlexboxGrid.Item as={Col} xs={24} md={12}>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t(constant.translation.cvMaker.formSchoolName)}
                    </Form.ControlLabel>
                    <Form.Control
                      name={`data[${idx}].universityName`}
                      placeholder="Select your school"
                      className="w-100"
                      searchable
                      accepter={SelectPicker}
                      data={_.map(universities, (u) => ({
                        label: _.get(u, 'name'),
                        value: _.get(u, 'name'),
                      }))}
                      defaultValue={_.get(_data, 'universityName')}
                      onOpen={() => {
                        if (universities.length === 0) {
                          this.getUniversities();
                        }
                      }}
                      onSearch={(keyword: string) => {
                        if (keyword !== '') {
                          this.getUniversities(keyword);
                        }
                      }}
                    />
                  </Form.Group>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item as={Col} xs={24} md={12}>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t(constant.translation.cvMaker.formDegree)}
                    </Form.ControlLabel>
                    <Form.Control
                      name={`data[${idx}].degree`}
                      placeholder="Select your degree"
                      className="w-100"
                      searchable={false}
                      accepter={SelectPicker}
                      defaultValue={_.get(_data, 'degree')}
                      data={_.map(degrees, (d) => ({
                        label: _.get(d, 'name'),
                        value: _.get(d, 'name'),
                      }))}
                    />
                  </Form.Group>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item as={Col} xs={24} md={12}>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t(constant.translation.cvMaker.formMajor)}
                    </Form.ControlLabel>
                    <Form.Control
                      name={`data[${idx}].major`}
                      onExit={() => this.getRecommEdu(idx)}
                      placeholder="Select your major"
                      className="w-100"
                      searchable
                      accepter={SelectPicker}
                      data={_.map(majors, (m) => ({
                        label: _.get(m, 'name'),
                        value: _.get(m, 'name'),
                      }))}
                      onOpen={() => {
                        if (majors.length === 0) {
                          this.getMajors();
                        }
                      }}
                      onSearch={(keyword: string) => {
                        if (keyword !== '') {
                          this.getMajors(keyword);
                        }
                      }}
                    />
                  </Form.Group>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item as={Col} xs={24} md={12}>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t(constant.translation.cvMaker.formLocation)}
                    </Form.ControlLabel>
                    <Form.Control
                      accepter={SelectPicker}
                      name={`data[${idx}].location`}
                      placeholder="Select location"
                      data={_.map(
                        _.get(this, 'state.countries'),
                        (val: any) => ({
                          label: val.name,
                          value: val.name,
                          flag: val.flag,
                        }),
                      )}
                      defaultValue={_.get(_data, 'location')}
                      renderMenuItem={(label, it: ItemDataType) => (
                        <div>
                          <img
                            src={_.get(it, 'flag')}
                            className="mr-4 w-1em img-flag"
                            alt={`flag-country-${it.name}`}
                          />
                          {label}
                        </div>
                      )}
                      className="w-100"
                    />
                  </Form.Group>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item as={Col} xs={24} md={12}>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t(constant.translation.cvMaker.formStartDate)}
                    </Form.ControlLabel>
                    <DatePicker
                      name={`data[${idx}].startDate`}
                      onSelect={(value) => {
                        this.onFormValueChanged(
                          moment(value).format('DD/MM/YYYY'),
                          `payload.educationData.data.${idx}.startDate`,
                        );
                      }}
                      shouldDisableDate={(date) =>
                        util.setPickerDisabledDate(_data, date, 'startDate')
                      }
                      oneTap
                      className="w-100"
                      placement="bottomStart"
                      placeholder="DD MM YYYY"
                      defaultValue={
                        _.get(_data, 'startDate')
                          ? moment(
                              _.get(_data, 'startDate'),
                              'DD/MM/YYYY',
                            ).toDate()
                          : null
                      }
                    />
                  </Form.Group>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item
                  as={Col}
                  xs={24}
                  md={12}
                  style={{ marginTop: '2.7em' }}
                >
                  <Form.Group>
                    <Form.ControlLabel>
                      {t(constant.translation.cvMaker.formEndDate)}
                    </Form.ControlLabel>
                    <DatePicker
                      name={`data[${idx}].endDate`}
                      onSelect={(value) => {
                        this.onFormValueChanged(
                          moment(value).format('DD/MM/YYYY'),
                          `payload.educationData.data[${idx}].endDate`,
                        );
                      }}
                      shouldDisableDate={(date) =>
                        util.setPickerDisabledDate(_data, date, 'endDate')
                      }
                      oneTap
                      placeholder="DD MM YYYY"
                      placement="bottomStart"
                      className="w-100"
                      defaultValue={
                        _.get(_data, 'endDate')
                          ? moment(
                              _.get(_data, 'endDate'),
                              'DD/MM/YYYY',
                            ).toDate()
                          : null
                      }
                      disabled={_.get(this.state, `toggleEdu.${idx}`)}
                    />
                    <Checkbox
                      onCheckboxClick={() => this.onToggleDisabledEdu(idx)}
                      defaultChecked={_.get(this.state, `toggleEdu.${idx}`)}
                    >
                      {t(
                        constant.translation.cvMaker
                          .endDateCheckboxEduPlaceholder,
                      )}
                    </Checkbox>
                  </Form.Group>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item as={Col} xs={24} md={12}>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t(constant.translation.cvMaker.formAchievements)}
                    </Form.ControlLabel>
                    <ReactQuill
                      ref={(e) => {
                        this.eduQuillRef[idx] = e?.getEditor();
                      }}
                      theme="snow"
                      value={_.get(
                        this.state,
                        `payload.educationData.data.${idx}.achievements`,
                      )}
                      placeholder={t(
                        constant.translation.cvMaker.formEduPlaceholder,
                      )}
                      onChange={(value: any) => {
                        const currData: Array<any> = _.get(
                          this,
                          'state.payload.educationData.data',
                          [],
                        );

                        if (currData.length <= idx) {
                          currData.push({});
                        }
                        currData[idx].achievements = value;

                        this.setState({
                          payload: {
                            ..._.get(this, 'state.payload'),
                            educationData: {
                              data: currData,
                            },
                          },
                        });
                      }}
                    />
                  </Form.Group>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item
                  as={Col}
                  xs={24}
                  md={12}
                  style={{ marginTop: '5rem' }}
                >
                  <Panel
                    shaded
                    bordered
                    className="ml-4 ai-work-profile-panel"
                    header={
                      <>
                        <Heading>
                          <img src="/icon-summary-ai.png" alt="icon-summary" />
                          {t(constant.translation.cvMaker.cvAIBuilderTitle)}
                        </Heading>
                        <Text muted>
                          {t(constant.translation.cvMaker.cvAIBuilderText3)}
                        </Text>
                      </>
                    }
                  >
                    <List
                      bordered
                      divider
                      hover
                      className="w-100 tg-ai-recommendation-list"
                      autoScroll
                    >
                      {(_.get(educations, idx) || []).map(
                        (recommendation: string) => (
                          <List.Item>
                            <Row className="w-100">
                              <Col xs={22}>{recommendation}</Col>
                              <Col xs={2}>
                                <IconButton
                                  circle
                                  onClick={() =>
                                    this.onAddEducationAchievementRecom(
                                      idx,
                                      recommendation,
                                    )
                                  }
                                  icon={<Plus />}
                                  appearance="default"
                                />
                              </Col>
                            </Row>
                          </List.Item>
                        ),
                      )}

                      {_.get(loadingEdu, idx) && (
                        <>
                          {[...Array(3)].map(() => (
                            <List.Item>
                              <Placeholder.Paragraph active />
                            </List.Item>
                          ))}
                          <Loader
                            center
                            content={t(
                              constant.translation.general
                                .formGenerateContentLoadingTextKey,
                            )}
                          />
                        </>
                      )}

                      {_.get(educations, `${idx}.length`, 0) === 0 &&
                        !_.get(loadingEdu, idx) && (
                          <FlexboxGrid
                            justify="center"
                            as={Col}
                            className="p-1em pt-2em pb-2em"
                          >
                            <FlexboxGrid.Item colspan={24}>
                              <img
                                src="/icon-recomm-not-found.png"
                                alt="icon-not-found"
                                className="d-grid"
                              />
                            </FlexboxGrid.Item>
                            <Text
                              muted
                              className="mt-8"
                              style={{ fontSize: '1em' }}
                            >
                              This section will populate automatically once you
                              complete the field above.
                            </Text>
                          </FlexboxGrid>
                        )}
                    </List>
                  </Panel>
                </FlexboxGrid.Item>
                <br />
              </FlexboxGrid>
            </Panel>
          ))}
        </Form>

        <Button
          appearance="link"
          className="mt-4 m-auto w-100"
          onClick={this.onAddMoreEducation}
        >
          <FaPlus /> &nbsp; Add Education
        </Button>
      </>
    );
  };

  renderSkillSection = (): React.ReactNode => {
    const { t } = this.props;
    const skillData: Array<any> =
      _.get(this, 'state.payload.skillData.data', []) || [];
    const recommendations = _.get(this, 'state.recommendations.skills') || {};
    const loadingSkills = _.get(this, 'state.loadingSkills');
    const toggleCVPreview = _.get(this, 'state.toggleCVPreview');

    return (
      <div>
        <Form
          key="skills-form"
          fluid
          onChange={(formValue) => {
            this.onFormValueChanged(formValue, 'payload.skillData');
          }}
        >
          <FlexboxGrid justify="start" align="middle">
            <FlexboxGrid.Item as={Col} xs={24} className="mb-8">
              <HeadingGroup className="cvmaker-title-header">
                <Heading>
                  <img src="/icon-skills.png" alt="icon-skills" />
                  {t(constant.translation.cvMaker.skillInfoTitle)}

                  <Button
                    appearance="link"
                    className="float-right"
                    onClick={() =>
                      this.setState({ toggleCVPreview: !toggleCVPreview })
                    }
                  >
                    <FaEye /> See{' '}
                    {t(constant.translation.cvMaker.cvPreviewTitle)}
                  </Button>
                </Heading>
                <Text muted>
                  {t(constant.translation.cvMaker.skillInfoSubtitle)}
                </Text>
              </HeadingGroup>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item as={Col} xs={24} md={13}>
              <Panel className="mt-8 skills-panel" shaded bordered>
                <Text muted>
                  {t(constant.translation.cvMaker.skillInfoDesc)}
                </Text>

                {/* Populate skill from parser */}
                {skillData.map((sk, i) => (
                  <Panel className="mt-4" shaded bordered>
                    <div className="d-ruby">
                      <InlineEdit
                        // eslint-disable-next-line react/no-array-index-key
                        key={`skillData${i}`}
                        defaultValue={sk}
                        showControls={false}
                        onSave={(e) => {
                          const data =
                            _.get(this.state, 'payload.skillData') || [];
                          const val = _.get(e, 'target.value');
                          _.set(data, `data.${i}`, val);

                          this.onFormValueChanged(data, 'payload.skillData');
                        }}
                      />
                      <IconButton
                        onClick={() => this.onDeleteSkill(i)}
                        appearance="subtle"
                        className="float-right remove-btn"
                        icon={<FaTrash />}
                      />
                    </div>
                  </Panel>
                ))}

                <Form.Group className="mt-8">
                  <Form.ControlLabel>
                    {t(constant.translation.cvMaker.formSkillName)}
                  </Form.ControlLabel>
                  <Form.Control
                    name="skillName"
                    value={_.get(this.state, 'payload.skillData.skillName')}
                    className="mt-4 w-50 text-transform-capitalize"
                    onPressEnter={() => this.onAddNewSkill()}
                    placeholder="Enter skill name"
                  />
                </Form.Group>
                <Button
                  appearance="link"
                  className="mt-4"
                  onClick={() => this.onAddNewSkill()}
                >
                  <FaPlus /> &nbsp; Add your own
                </Button>
              </Panel>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item as={Col} xs={24} md={11}>
              <Panel
                shaded
                bordered
                className="ml-4 ai-work-profile-panel 
                ai-work-profile-panel-scroll"
                header={
                  <>
                    <Heading>
                      <img src="/icon-summary-ai.png" alt="icon-summary" />
                      {t(constant.translation.cvMaker.cvAIBuilderTitle)}
                    </Heading>
                    <Text muted>
                      {t(constant.translation.cvMaker.cvAIBuilderText)}
                    </Text>
                  </>
                }
              >
                {loadingSkills && (
                  <List
                    bordered
                    divider
                    hover
                    className="w-100 tg-ai-recommendation-list"
                  >
                    {[...Array(3)].map(() => (
                      <List.Item>
                        <Placeholder.Paragraph active />
                      </List.Item>
                    ))}
                    <Loader
                      center
                      content={t(
                        constant.translation.general
                          .formGenerateContentLoadingTextKey,
                      )}
                    />
                  </List>
                )}

                {Object.keys(recommendations).map((group) => (
                  <>
                    <Text className="tg-ai-recommendation-list-group">
                      {group}
                    </Text>
                    <List bordered divider hover>
                      {(_.get(recommendations, group) || []).map(
                        (recomm: string) => (
                          <List.Item>
                            <Row className="w-100">
                              <Col xs={22}>{recomm}</Col>
                              <Col xs={2}>
                                <IconButton
                                  circle
                                  onClick={() => this.onAddNewSkill(recomm)}
                                  icon={<Plus />}
                                  appearance="default"
                                />
                              </Col>
                            </Row>
                          </List.Item>
                        ),
                      )}
                    </List>
                  </>
                ))}
              </Panel>
            </FlexboxGrid.Item>
          </FlexboxGrid>
        </Form>
        <br />
      </div>
    );
  };

  renderExtraSection = (): React.ReactNode => {
    const { t } = this.props;
    const { toggleCVPreview, payload } = this.state;
    const extraSectionData = _.get(payload, 'extraSectionData');

    return (
      <div>
        <Form
          key="extra-data-form"
          fluid
          onChange={(formValue) => {
            this.onFormValueChanged(formValue, 'payload.extraData');
          }}
        >
          <FlexboxGrid justify="start" align="top">
            <FlexboxGrid.Item as={Col} xs={24} className="mb-8">
              <HeadingGroup className="cvmaker-title-header">
                <Heading>
                  <img src="/icon-extra-section.png" alt="icon-skills" />
                  {t(constant.translation.cvMaker.extraSectionTitle)}

                  <Button
                    appearance="link"
                    className="float-right"
                    onClick={() =>
                      this.setState({ toggleCVPreview: !toggleCVPreview })
                    }
                  >
                    <FaEye /> See{' '}
                    {t(constant.translation.cvMaker.cvPreviewTitle)}
                  </Button>
                </Heading>
                <Text muted>
                  {t(constant.translation.cvMaker.extraSectionSubtitle)}
                </Text>
              </HeadingGroup>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item as={Col} xs={12}>
              <Panel className="mt-8" defaultExpanded bordered>
                <Row>
                  <Col xs={8}>
                    <div className="d-webkit-box">
                      <img
                        src="/icon-certif-and-course.png"
                        className="mr-4 w-2em"
                        alt="icon-certif-course"
                      />
                      <p className="mt-2" style={{ fontSize: '.9rem' }}>
                        {t(
                          constant.translation.cvMaker
                            .extraSectionCertification,
                        )}
                      </p>
                    </div>
                  </Col>
                  <Col xs={6} xsPush={10}>
                    <IconButton
                      className="float-right mt-2"
                      icon={<FaPlus />}
                      appearance="link"
                      onClick={() => this.onAddNewExtraSection('certificate')}
                    />
                  </Col>
                </Row>
              </Panel>
              <Panel className="mt-8" defaultExpanded bordered>
                <Row>
                  <Col xs={8}>
                    <div className="d-webkit-box">
                      <img
                        src="/icon-language.png"
                        className="mr-4 w-2em"
                        alt="icon-certif-course"
                      />
                      <p className="mt-2" style={{ fontSize: '.9rem' }}>
                        {t(constant.translation.cvMaker.extraSectionLanguage)}
                      </p>
                    </div>
                  </Col>
                  <Col xs={6} xsPush={10}>
                    <IconButton
                      className="float-right mt-2"
                      icon={<FaPlus />}
                      appearance="link"
                      onClick={() => this.onAddNewExtraSection('language')}
                    />
                  </Col>
                </Row>
              </Panel>
              <Panel className="mt-8" defaultExpanded bordered>
                <Row>
                  <Col xs={8}>
                    <div className="d-webkit-box">
                      <img
                        src="/icon-projects.png"
                        className="mr-4 w-2em"
                        alt="icon-certif-course"
                      />
                      <p className="mt-2" style={{ fontSize: '.9rem' }}>
                        {t(constant.translation.cvMaker.extraSectionProjects)}
                      </p>
                    </div>
                  </Col>
                  <Col xs={6} xsPush={10}>
                    <IconButton
                      className="float-right mt-2"
                      icon={<FaPlus />}
                      appearance="link"
                      onClick={() => this.onAddNewExtraSection('project')}
                    />
                  </Col>
                </Row>
              </Panel>
              <Panel className="mt-8" defaultExpanded bordered>
                <Row>
                  <Col xs={8}>
                    <div className="d-webkit-box">
                      <img
                        src="/icon-volunteer.png"
                        className="mr-4 w-2em"
                        alt="icon-certif-course"
                      />
                      <p className="mt-2" style={{ fontSize: '.9rem' }}>
                        {t(constant.translation.cvMaker.extraSectionVolunteer)}
                      </p>
                    </div>
                  </Col>
                  <Col xs={6} xsPush={10}>
                    <IconButton
                      className="float-right mt-2"
                      icon={<FaPlus />}
                      appearance="link"
                      onClick={() => this.onAddNewExtraSection('volunteer')}
                    />
                  </Col>
                </Row>
              </Panel>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item as={Col} xs={12}>
              <Panel
                className="extra-section-panel mt-8"
                defaultExpanded
                bordered
              >
                {_.get(extraSectionData, 'certifications', []).length === 0 &&
                  _.get(extraSectionData, 'languages', []).length === 0 &&
                  _.get(extraSectionData, 'projects', []).length === 0 &&
                  _.get(extraSectionData, 'volunteers', []).length === 0 && (
                    <Text className="tg-ai-recommendation-list-group">
                      {t(
                        constant.translation.cvMaker
                          .extraSectionEmptyPlaceholder,
                      )}
                    </Text>
                  )}
                {_.get(extraSectionData, 'certifications', []).length > 0 && (
                  <div>
                    <Text className="tg-ai-recommendation-list-group">
                      {t(
                        constant.translation.cvMaker.extraSectionCertification,
                      )}
                    </Text>
                    {_.map(
                      _.get(extraSectionData, 'certifications') || [],
                      (d: CertificationData, id: number) => (
                        <List divider hover>
                          <List.Item>
                            <li>
                              <ul>
                                <InlineEdit
                                  defaultValue={d.name || '[Certificate Name]'}
                                  showControls={false}
                                  onSave={(e) =>
                                    this.onUpdateExtraSection(
                                      e,
                                      id,
                                      'certifications',
                                      'name',
                                    )
                                  }
                                />
                                <span className="float-right mr-4">
                                  <IconButton
                                    icon={<FaTrash />}
                                    className="remove-btn"
                                    onClick={() =>
                                      this.onDeleteExtra(id, 'certificate')
                                    }
                                  />
                                </span>
                              </ul>
                            </li>
                            <li>
                              <ul>
                                <InlineEdit
                                  defaultValue={d.issuer || '[Issuer]'}
                                  showControls={false}
                                  onSave={(e) =>
                                    this.onUpdateExtraSection(
                                      e,
                                      id,
                                      'certifications',
                                      'issuer',
                                    )
                                  }
                                />{' '}
                                |{' '}
                                <InlineEdit
                                  defaultValue={d.year || '[Year]'}
                                  showControls={false}
                                  onSave={(e) =>
                                    this.onUpdateExtraSection(
                                      e,
                                      id,
                                      'certifications',
                                      'year',
                                    )
                                  }
                                />
                              </ul>
                            </li>
                          </List.Item>
                        </List>
                      ),
                    )}
                  </div>
                )}
                {_.get(extraSectionData, 'languages', []).length > 0 && (
                  <div>
                    <Text className="tg-ai-recommendation-list-group">
                      {t(constant.translation.cvMaker.extraSectionLanguage)}
                    </Text>
                    {_.map(
                      _.get(extraSectionData, 'languages') || [],
                      (d: LanguageData, id: number) => (
                        <List divider hover>
                          <List.Item>
                            <li>
                              <ul>
                                <InlineEdit
                                  defaultValue={d.language || '[Language]'}
                                  showControls={false}
                                  onSave={(e) =>
                                    this.onUpdateExtraSection(
                                      e,
                                      id,
                                      'languages',
                                      'language',
                                    )
                                  }
                                />{' '}
                                -{' '}
                                <InlineEdit
                                  defaultValue={
                                    d.proficient ||
                                    // eslint-disable-next-line max-len
                                    '[Proficient Lvl: Elementary | Limited working | Professional | Native]'
                                  }
                                  showControls={false}
                                  onSave={(e) =>
                                    this.onUpdateExtraSection(
                                      e,
                                      id,
                                      'languages',
                                      'proficient',
                                    )
                                  }
                                />
                                <span className="float-right mr-4">
                                  <IconButton
                                    className="remove-btn"
                                    icon={<FaTrash />}
                                    onClick={() =>
                                      this.onDeleteExtra(id, 'language')
                                    }
                                  />
                                </span>
                              </ul>
                            </li>
                          </List.Item>
                        </List>
                      ),
                    )}
                  </div>
                )}
                {_.get(extraSectionData, 'projects', []).length > 0 && (
                  <div>
                    <Text className="tg-ai-recommendation-list-group">
                      {t(constant.translation.cvMaker.extraSectionProjects)}
                    </Text>
                    {_.map(
                      _.get(extraSectionData, 'projects') || [],
                      (d: ProjectData, id: number) => (
                        <List divider hover>
                          <List.Item>
                            <li>
                              <ul>
                                <InlineEdit
                                  defaultValue={d.name || '[Project Name]'}
                                  showControls={false}
                                  onSave={(e) =>
                                    this.onUpdateExtraSection(
                                      e,
                                      id,
                                      'projects',
                                      'name',
                                    )
                                  }
                                />{' '}
                                |{' '}
                                <InlineEdit
                                  defaultValue={d.year || '[Year]'}
                                  showControls={false}
                                  onSave={(e) =>
                                    this.onUpdateExtraSection(
                                      e,
                                      id,
                                      'projects',
                                      'year',
                                    )
                                  }
                                />
                              </ul>
                              <span className="float-right mr-4">
                                <IconButton
                                  className="remove-btn"
                                  icon={<FaTrash />}
                                  onClick={() =>
                                    this.onDeleteExtra(id, 'project')
                                  }
                                />
                              </span>
                            </li>
                            <li>
                              <ul>
                                <InlineEdit
                                  defaultValue="Role:"
                                  showControls={false}
                                  onSave={() => null}
                                  disabled
                                  className="role"
                                />
                                <InlineEdit
                                  defaultValue={d.role || '[Your Role]'}
                                  showControls={false}
                                  onSave={(e) =>
                                    this.onUpdateExtraSection(
                                      e,
                                      id,
                                      'projects',
                                      'role',
                                    )
                                  }
                                />
                              </ul>
                            </li>
                            <li>
                              <ul>
                                <InlineEdit
                                  defaultValue="Contribution:"
                                  showControls={false}
                                  onSave={() => null}
                                  disabled
                                  className="title"
                                />
                                <InlineEdit
                                  defaultValue={
                                    d.contribution || '[Quantifiable Result]'
                                  }
                                  showControls={false}
                                  onSave={(e) =>
                                    this.onUpdateExtraSection(
                                      e,
                                      id,
                                      'projects',
                                      'contribution',
                                    )
                                  }
                                />
                              </ul>
                            </li>
                          </List.Item>
                        </List>
                      ),
                    )}
                  </div>
                )}
                {_.get(extraSectionData, 'volunteers', []).length > 0 && (
                  <div>
                    <Text className="tg-ai-recommendation-list-group">
                      {t(constant.translation.cvMaker.extraSectionVolunteer)}
                    </Text>
                    {_.map(
                      _.get(extraSectionData, 'volunteers') || [],
                      (d: VolunteerData, id: number) => (
                        <List divider hover>
                          <List.Item>
                            <li>
                              <ul>
                                <InlineEdit
                                  defaultValue={d.name || '[Organization]'}
                                  showControls={false}
                                  onSave={(e) =>
                                    this.onUpdateExtraSection(
                                      e,
                                      id,
                                      'volunteers',
                                      'name',
                                    )
                                  }
                                />{' '}
                                |{' '}
                                <InlineEdit
                                  defaultValue={d.duration || '[Duration]'}
                                  showControls={false}
                                  onSave={(e) =>
                                    this.onUpdateExtraSection(
                                      e,
                                      id,
                                      'volunteers',
                                      'duration',
                                    )
                                  }
                                />
                              </ul>
                            </li>
                            <li>
                              <ul>
                                <InlineEdit
                                  defaultValue="Role:"
                                  showControls={false}
                                  onSave={() => null}
                                  disabled
                                  className="title"
                                />
                                <InlineEdit
                                  defaultValue={d.role || '[Your Role]'}
                                  showControls={false}
                                  onSave={(e) =>
                                    this.onUpdateExtraSection(
                                      e,
                                      id,
                                      'volunteers',
                                      'role',
                                    )
                                  }
                                />
                                <span className="float-right mr-4">
                                  <IconButton
                                    className="remove-btn"
                                    icon={<FaTrash />}
                                    onClick={() =>
                                      this.onDeleteExtra(id, 'volunteer')
                                    }
                                  />
                                </span>
                              </ul>
                            </li>
                            <li>
                              <ul>
                                <InlineEdit
                                  defaultValue="Contribution:"
                                  showControls={false}
                                  disabled
                                  className="title"
                                />
                                <InlineEdit
                                  defaultValue={
                                    d.contribution || '[Quantifiable Result]'
                                  }
                                  showControls={false}
                                  onSave={(e) =>
                                    this.onUpdateExtraSection(
                                      e,
                                      id,
                                      'volunteers',
                                      'contribution',
                                    )
                                  }
                                />
                              </ul>
                            </li>
                          </List.Item>
                        </List>
                      ),
                    )}
                  </div>
                )}
              </Panel>
            </FlexboxGrid.Item>
          </FlexboxGrid>
        </Form>
      </div>
    );
  };

  render(): React.ReactNode | null {
    const { step, isLoading, toggleCVPreview, promo } = this.state;
    const { t } = this.props;

    if (isLoading) {
      return (
        <Loader
          backdrop
          content={t(constant.translation.general.formLoadingTextKey)}
          vertical
        />
      );
    }

    const user = AuthStorage.getInstance().getUser();

    return (
      <div className="cv-maker-steps-container">
        <Avatar
          circle
          size="md"
          src={
            _.get(user, 'avatar.url') ||
            // eslint-disable-next-line max-len
            'https://cdn.icon-icons.com/icons2/1378/PNG/512/avatardefault_92824.png'
          }
          className="avatar-pos-abs"
          alt="img-user-profile"
        />
        {this.renderModal()}

        <Steps className="cv-maker-step" current={step - 2}>
          <Steps.Item title="Personal Data" />
          <Steps.Item title="Professional Summary" />
          <Steps.Item title="Experience" />
          <Steps.Item title="Education" />
          <Steps.Item title="Skills" />
          <Steps.Item title="Extra Sections" />
        </Steps>

        <Panel className={`cv-maker-steps-panel ${step > 2 ? 'w-100' : ''}`}>
          {step === 2 && this.renderPersonalDataSection()}
          {step === 3 && this.renderProfessionalSection()}
          {step === 4 && this.renderExperienceSection()}
          {step === 5 && this.renderEducationSection()}
          {step === 6 && this.renderSkillSection()}
          {step === 7 && this.renderExtraSection()}
        </Panel>

        <ResumePreviewer
          {...(_.get(promo, 'active')
            ? {
                title: `FREE charge with code "${_.get(
                  promo,
                  'code',
                )}" until ${_.get(promo, 'formattedExpiredAt')}`,
              }
            : { title: undefined })}
          isOpen={toggleCVPreview}
          onClose={() => this.setState({ toggleCVPreview: !toggleCVPreview })}
        >
          <ResumeTemplate.Onyx
            isPreview
            data={{
              name: _.startCase(
                _.get(this.state, 'payload.personalData.fullName', 'Your Name'),
              ),
              tagline: _.startCase(
                _.get(
                  this.state,
                  'payload.personalData.jobTitle',
                  'Your current role',
                ),
              ),
              contacts: [
                {
                  text: `${_.get(
                    this.state,
                    'payload.personalData.city',
                    'Your city',
                  )}, ${_.get(
                    this.state,
                    'payload.personalData.country',
                    'Your country',
                  )}`,
                },
                {
                  text: _.get(
                    this.state,
                    'payload.personalData.email',
                    'your_email@gmail.com',
                  ),
                  href: `mailto:${_.get(
                    this.state,
                    'payload.personalData.email',
                    'your_email@gmail.com',
                  )}`,
                },
                {
                  text: `${_.get(
                    this.state,
                    'payload.personalData.phoneRegion',
                    '+62',
                  )} ${_.get(
                    this.state,
                    'payload.personalData.phoneNumber',
                    '811-1111-1111',
                  )}`,
                },
                {
                  text: String(
                    _.get(
                      this.state,
                      'payload.personalData.linkedInProfile',
                      'linkedin.com/in/john-doe',
                    ),
                  )
                    .replace(/^https?:\/\//, '')
                    .replace(/^www\./, ''),
                  href: _.get(
                    this.state,
                    'payload.personalData.linkedInProfile',
                    'https://linkedin.com/in/john-doe',
                  ),
                },
              ],
              summary:
                util.parseHtmlBulletAsString(
                  _.get(this.state, 'payload.professionalData.summary') || '',
                ) || t(constant.translation.cvMaker.formSummaryDefault),
              experience:
                _.get(this.state, 'payload.experienceData.data', []).length > 0
                  ? _.map(
                      _.get(this.state, 'payload.experienceData.data', []),
                      (d, id) => ({
                        company: _.startCase(
                          _.get(d, 'employer', 'Your company'),
                        ),
                        role: _.startCase(_.get(d, 'jobTitle', 'Your role')),
                        start: moment(
                          _.get(d, 'startDate', '01/08/2015'),
                          'DD/MM/YYYY',
                        ).format('MMM YYYY'),
                        end:
                          _.get(d, 'endDate') &&
                          moment().format('DD/MM/YYYY') !== _.get(d, 'endDate')
                            ? moment(_.get(d, 'endDate'), 'DD/MM/YYYY').format(
                                'MMM YYYY',
                              )
                            : 'Now',
                        jobType: _.get(d, 'jobType', 'Full Time'),
                        location: _.get(d, 'location', 'Unknown Location'),
                        bullets: util.parseHtmlBullet(
                          _.get(
                            this.state,
                            `payload.experienceData.data[${id}].jobDesc`,
                          ) ||
                            t(
                              constant.translation.cvMaker
                                .formLoremIpsumShorter,
                            ),
                        ),
                      }),
                    )
                  : [
                      {
                        company: 'Your company',
                        role: 'Your role',
                        start: 'Jan 1997',
                        end: 'Now',
                        jobType: 'Full Time',
                        location: 'Indonesia',
                        bullets: [
                          t(constant.translation.cvMaker.formLoremIpsumShorter),
                          t(constant.translation.cvMaker.formLoremIpsumShorter),
                        ],
                      },
                    ],
              projects: _.map(
                _.get(this.state, 'payload.extraSectionData.projects') || [],
                (it: ProjectData) => ({
                  name: it.name || '[Project Name]',
                  role: it.role || '[Your Role]',
                  year: it.year || '[Year]',
                  blurb: it.contribution || '[Contribution or Impact]',
                }),
              ),
              certifications: _.map(
                _.get(this.state, 'payload.extraSectionData.certifications') ||
                  [],
                (it: CertificationData) => ({
                  title: it.name || '[Certificate Name]',
                  org: it.issuer || '[Issuer]',
                  year: it.year || '[Year]',
                }),
              ),
              volunteers: _.map(
                _.get(this.state, 'payload.extraSectionData.volunteers') || [],
                (it: VolunteerData) => ({
                  name: it.name || '[Project Name]',
                  role: it.role || '[Your Role]',
                  duration: it.duration || '[Duration]',
                  blurb: it.contribution || '[Contribution or Impact]',
                }),
              ),
              skills: _.get(this.state, 'payload.skillData.data') || [],
              languages: _.map(
                _.get(this.state, 'payload.extraSectionData.languages') || [],
                (it: LanguageData) =>
                  `${it.language || '[Language]'} (${
                    it.proficient || '[Proficiency]'
                  })`,
              ),
              education:
                _.get(this.state, 'payload.educationData.data', []).length > 0
                  ? _.map(
                      _.get(this.state, 'payload.educationData.data', []),
                      (d, id: number) => ({
                        school: _.get(d, 'universityName', 'Your University'),
                        degree: _.get(d, 'degree', 'Unknown degree'),
                        major: _.get(d, 'major', 'Your Major'),
                        start: moment(
                          _.get(d, 'startDate', '01/08/2015'),
                          'DD/MM/YYYY',
                        ).format('MMM YYYY'),
                        end:
                          _.get(d, 'endDate') &&
                          moment().format('DD/MM/YYYY') !== _.get(d, 'endDate')
                            ? moment(_.get(d, 'endDate'), 'DD/MM/YYYY').format(
                                'MMM YYYY',
                              )
                            : 'Now',
                        location: _.get(d, 'location', 'Indonesia'),
                        achievement: util.parseHtmlBulletAsString(
                          _.get(
                            this.state,
                            `payload.educationData.data[${id}].achievements`,
                          ) ||
                            t(
                              constant.translation.cvMaker
                                .formLoremIpsumShorter,
                            ),
                        ),
                      }),
                    )
                  : [
                      {
                        school: 'Your University',
                        major: 'Your Major',
                        degree: 'Unknown degree',
                        start: 'Aug 2015',
                        end: 'Now',
                        location: 'Indonesia',
                        achievement: t(
                          constant.translation.cvMaker.formLoremIpsumShorter,
                        ),
                      },
                    ],
            }}
          />
        </ResumePreviewer>

        {/* Bottom Docker */}
        <BottomDocker
          onCancelClicked={this.onCancelClicked}
          onContinueClicked={this.onContinueClicked}
          continueLabel={_.get(nextStateMap, step)}
          cancelLabel={_.get(prevStateMap, step)}
          threshold={10}
        />
      </div>
    );
  }
}

export default withRouter(withTranslation()(CVMakerBuilderScreen));
